import { type ReactNode, useCallback, useEffect, useState } from "react";
import { unlockOnGesture } from "./audio/sounds";
import { useMeasure } from "./canvas/useMeasure";
import { useDeckPreload } from "./components/Card";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { watchInput } from "./components/useModalFocus";
import { HandshakeFailed, handshake } from "./discord/handshake";
import { presenceFor, pushPresence } from "./discord/presence";
import { useDepartureReports } from "./discord/useDepartureReports";
import { useSpeaking } from "./discord/useSpeaking";
import { LocaleChoiceContext, LocaleContext } from "./i18n/context";
import { initialLocale, LOCALE_KEY, type Locale } from "./i18n/index";
import { reportError } from "./ops/report";
import { Authorize } from "./routes/authorize";
import { Home } from "./routes/home";
import { Lobby } from "./routes/lobby";
import { Table } from "./routes/table";
import { Update } from "./routes/update";
import { useStore } from "./store";
import { openTable, setRestart, setSocket } from "./ws/singleton";

/** The picked language from this device, when storage works (a private window may refuse it). */
function storedLocale(): string | null {
  try {
    return localStorage.getItem(LOCALE_KEY);
  } catch {
    return null;
  }
}

/** Handshake → socket → screen by table status. */
export function App() {
  const measure = useMeasure();
  const phase = useStore((s) => s.phase);
  const snapshot = useStore((s) => s.snapshot);
  const stale = useStore((s) => s.stale);
  const lastError = useStore((s) => s.lastError);
  const set = useStore((s) => s.set);
  const [locale, setLocale] = useState<Locale>(() =>
    initialLocale(storedLocale(), navigator.languages),
  );
  // Screen readers and Arabic text direction follow the document language (review 0021).
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const chooseLocale = useCallback((next: Locale) => {
    setLocale(next);
    try {
      localStorage.setItem(LOCALE_KEY, next);
    } catch {
      // Without storage the choice lasts until the Activity closes.
    }
  }, []);
  useDeckPreload();
  useDepartureReports();
  useSpeaking();
  useEffect(() => {
    watchInput();
    return unlockOnGesture();
  }, []);

  const start = useCallback(async () => {
    set({ phase: "authorizing", error: null });
    try {
      const result = await handshake((stage) => set({ stage }));
      set({
        phase: "ready",
        member: result.member,
        guildId: result.guildId,
        sdk: result.sdk,
        session: result.session,
        instanceId: result.instanceId,
      });
      // An Activity link carries the table to join; otherwise this channel's own table.
      openTable(result.customId ?? result.instanceId);
    } catch (error) {
      const failed = error instanceof HandshakeFailed ? error : null;
      // Where the Activity fails to open is where the server hears nothing else (review 0019 U7).
      const eventId = reportError("handshake", error, {
        stage: failed?.code ?? "server",
        platform: failed?.diagnostics?.platform ?? undefined,
      });
      set({
        phase: "failed",
        error: failed?.code ?? "server",
        diagnostics: failed?.diagnostics ?? null,
        eventId,
      });
    }
  }, [set]);

  useEffect(() => {
    setRestart(() => void start());
    void start();
    return () => {
      setRestart(null);
      setSocket(null);
    };
  }, [start]);

  // Rich Presence follows the table; the sender skips unchanged text.
  const sdk = useStore((s) => s.sdk);
  useEffect(() => {
    if (snapshot !== null) void pushPresence(sdk, presenceFor(snapshot), Date.now());
  }, [sdk, snapshot]);

  let screen: ReactNode;
  if (stale) screen = <Update />;
  else if (phase !== "ready" || snapshot === null)
    screen = <Authorize onRetry={() => void start()} />;
  else if (snapshot.table.status === "lobby" || snapshot.table.status === "countdown") {
    // An empty table shows the game choice to whoever opens it; a seated table is a lobby.
    const empty = snapshot.table.seats.every((seat) => seat === null);
    screen = empty ? (
      <Home snapshot={snapshot} measure={measure} />
    ) : (
      <Lobby snapshot={snapshot} measure={measure} lastError={lastError} />
    );
  } else screen = <Table snapshot={snapshot} measure={measure} lastError={lastError} />;

  return (
    <LocaleContext.Provider value={locale}>
      <LocaleChoiceContext.Provider value={chooseLocale}>
        <ErrorBoundary locale={locale}>{screen}</ErrorBoundary>
      </LocaleChoiceContext.Provider>
    </LocaleContext.Provider>
  );
}
